import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, BedDouble } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useApp } from "@/context/AppContext";

export default function Admissions() {
  const { fq, user } = useApp();
  const nav = useNavigate();
  const [rows, setRows] = useState([]);
  const [patients, setPatients] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    patient_id: "", doctor_id: "", admission_date: new Date().toISOString().slice(0, 10),
    ward: "General Ward", room: "", bed: "", diagnosis: "", notes: "",
  });

  const load = () => api.get(`/admissions${fq ? `?${fq}` : ""}`).then(({ data }) => setRows(data));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [fq]);
  useEffect(() => {
    api.get("/patients").then(({ data }) => setPatients(data));
    api.get("/users?role=doctor").then(({ data }) => setDoctors(data));
  }, []);

  const save = async () => {
    if (!form.patient_id || !form.doctor_id) return toast.error("Select patient and consultant");
    try {
      const { data } = await api.post("/admissions", {
        ...form, patient_id: Number(form.patient_id), doctor_id: Number(form.doctor_id),
      });
      toast.success(`Admitted — ${data.ip_number}`);
      setOpen(false); load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const canAdmit = ["super_admin", "doctor"].includes(user.role);

  return (
    <div className="space-y-5" data-testid="admissions-page">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <p className="text-xs font-mono uppercase tracking-widest text-primary">Inpatient · IPD</p>
          <h1 className="font-heading text-3xl font-bold tracking-tight">Admissions</h1>
        </div>
        {canAdmit && (
          <Button className="gap-2" onClick={() => setOpen(true)} data-testid="admit-patient-button">
            <Plus className="h-4 w-4" /> Admit patient
          </Button>
        )}
      </div>

      <Card className="shadow-sm">
        <CardContent className="p-0">
          <Table data-testid="admissions-table">
            <TableHeader><TableRow>
              <TableHead>IP number</TableHead><TableHead>Patient</TableHead><TableHead>Consultant</TableHead>
              <TableHead>Ward / Room / Bed</TableHead><TableHead>Admitted</TableHead>
              <TableHead>Diagnosis</TableHead><TableHead>Status</TableHead>
              <TableHead className="text-right">Action</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {rows.length === 0 && (
                <TableRow><TableCell colSpan={8} className="text-center py-10 text-sm text-muted-foreground">
                  No admissions recorded.
                </TableCell></TableRow>
              )}
              {rows.map((a) => (
                <TableRow key={a.id} data-testid={`admission-row-${a.id}`} className="hover:bg-muted/50">
                  <TableCell className="font-mono text-xs">{a.ip_number}</TableCell>
                  <TableCell className="font-medium">{a.patient_name}
                    <span className="block text-xs font-mono text-muted-foreground">{a.patient_uhid}</span>
                  </TableCell>
                  <TableCell>{a.doctor_name}</TableCell>
                  <TableCell className="text-xs">{a.ward} / {a.room || "—"} / {a.bed || "—"}</TableCell>
                  <TableCell className="text-xs font-mono">{a.admission_date}</TableCell>
                  <TableCell className="max-w-[200px] truncate">{a.diagnosis}</TableCell>
                  <TableCell>
                    <Badge variant={a.status === "Admitted" ? "default" : "secondary"}>{a.status}</Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    <Button size="sm" variant="outline" className="gap-1.5" data-testid={`open-admission-${a.id}`}
                      onClick={() => nav(`/admissions/${a.id}`)}>
                      <BedDouble className="h-3.5 w-3.5" /> Open chart
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="admission-dialog">
          <DialogHeader><DialogTitle className="font-heading">Admit patient</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label className="text-xs">Patient</Label>
              <Select value={form.patient_id} onValueChange={(v) => setForm({ ...form, patient_id: v })}>
                <SelectTrigger data-testid="admission-patient-select"><SelectValue placeholder="Select patient" /></SelectTrigger>
                <SelectContent>
                  {patients.map((p) => <SelectItem key={p.id} value={String(p.id)}>{p.name} · {p.uhid}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Consultant</Label>
              <Select value={form.doctor_id} onValueChange={(v) => setForm({ ...form, doctor_id: v })}>
                <SelectTrigger data-testid="admission-doctor-select"><SelectValue placeholder="Select doctor" /></SelectTrigger>
                <SelectContent>
                  {doctors.map((d) => <SelectItem key={d.id} value={String(d.id)}>{d.full_name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-4 gap-3">
              <div className="space-y-1.5"><Label className="text-xs">Date</Label>
                <Input type="date" value={form.admission_date}
                  onChange={(e) => setForm({ ...form, admission_date: e.target.value })} /></div>
              <div className="space-y-1.5"><Label className="text-xs">Ward</Label>
                <Input value={form.ward} data-testid="admission-ward-input"
                  onChange={(e) => setForm({ ...form, ward: e.target.value })} /></div>
              <div className="space-y-1.5"><Label className="text-xs">Room</Label>
                <Input value={form.room} data-testid="admission-room-input"
                  onChange={(e) => setForm({ ...form, room: e.target.value })} /></div>
              <div className="space-y-1.5"><Label className="text-xs">Bed</Label>
                <Input value={form.bed} data-testid="admission-bed-input"
                  onChange={(e) => setForm({ ...form, bed: e.target.value })} /></div>
            </div>
            <div className="space-y-1.5"><Label className="text-xs">Provisional diagnosis</Label>
              <Input value={form.diagnosis} data-testid="admission-diagnosis-input"
                onChange={(e) => setForm({ ...form, diagnosis: e.target.value })} /></div>
            <div className="space-y-1.5"><Label className="text-xs">Admission notes</Label>
              <Textarea rows={3} value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} data-testid="save-admission-button">Admit</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
